import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Modal } from "react-bootstrap"
import ReactApexChart from "react-apexcharts"
import Rightbar from "pages/main/cheques/Rightbar"

import { useNavigate } from "react-router"

import { httpOk, GET } from "helpers/api"
import { formatDate, formatMoney, regexPhoneNumber } from "helpers/helpers"
import { hasPermission } from "helpers/permissions"
import AnalyticsFilterBar, { FilterSelect } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { setBusinessDayStartFromPoses } from "helpers/businessDay"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "18px",
	boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const toneMap = {
	primary: { color: "#2563eb", bg: "var(--dark-tint-primary, #eaf2ff)" },
	success: { color: "#16a34a", bg: "var(--dark-tint-success, #e7f8ef)" },
	warning: { color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)" },
	danger: { color: "#dc2626", bg: "var(--dark-tint-danger, #fdecec)" },
	violet: { color: "#7c3aed", bg: "var(--dark-tint-primary, #f1eafe)" },
	pink: { color: "#ec4899", bg: "var(--dark-tint-danger, #fce7f3)" },
}

const kpiTitleKeys = {
	sales: "sales",
	cheques: "cheques",
	average_cheque: "average_cheque",
	gross_income: "gross_income",
	expenses: "expenses",
	discount: "discounts",
}

// Вебу нужны только три топа — остальные разделы сервер не считает (приложение владельца просит все)
const TOP_INCLUDE = "order_types,menus,products"

const DAY_PRESETS = ["today", "yesterday", "7days", "30days", "this_month", "custom"]

function fullNumber(value, digits = 0) {
	return formatMoney(Number(value || 0), digits)
}

function formatGrowth(value) {
	if (value === null || value === undefined) return "новое"
	return `${Number(value) >= 0 ? "+" : ""}${Number(value).toFixed(1).replace(".0", "")}%`
}

function KpiCard({ item, title, onClick, compareLabel }) {
	const tone = toneMap[item.tone] || toneMap.primary
	const clickable = typeof onClick === "function"
	const hasCompare = item.compare_value !== null && item.compare_value !== undefined
	const growth = item.growth_percent
	const isNew = growth === null || growth === undefined
	const isGood = item.inverse ? Number(growth) <= 0 : Number(growth) >= 0
	return (
		<div
			className="card h-100"
			style={{ ...cardStyle, ...(clickable ? { cursor: "pointer" } : {}) }}
			onClick={onClick}
			role={clickable ? "button" : undefined}
		>
			<div className="card-body" style={{ padding: "12px" }}>
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "12px", background: tone.bg, color: tone.color, flex: "0 0 32px" }}>
						<i className={`uil ${item.icon}`} style={{ fontSize: "17px" }}></i>
					</div>
				</div>
				<div className="mt-2" style={{ fontSize: "22px", lineHeight: 1.15, fontWeight: 900, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.4px", whiteSpace: "nowrap" }}>{fullNumber(item.value)}</div>
				{hasCompare && (
					<div className="mt-1" style={{ fontSize: "12px" }} title={`${compareLabel || ""}: ${fullNumber(item.compare_value)}`}>
						<span className={isNew ? "text-muted" : isGood ? "text-success" : "text-danger"} style={{ fontWeight: 700 }}>
							{formatGrowth(growth)}
						</span>
						<span className="text-muted ms-1">{compareLabel}</span>
					</div>
				)}
			</div>
		</div>
	)
}

function SalesChart({ chart, t, subtitle }) {
	const labels = chart?.labels || []
	const options = {
		chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
		colors: ["#2563eb", "#f59f00"],
		dataLabels: { enabled: false },
		stroke: { curve: "smooth", width: [4, 3] },
		fill: {
			type: "gradient",
			gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.04, stops: [0, 90, 100] },
		},
		grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
		xaxis: { categories: labels, tooltip: { enabled: false }, labels: { style: { colors: "#8791a1" } } },
		yaxis: [
			{ labels: { formatter: value => fullNumber(value), style: { colors: "#8791a1" } } },
			{ opposite: true, labels: { formatter: value => formatMoney(value, 0), style: { colors: "#8791a1" } } },
		],
		legend: { position: "top", horizontalAlign: "right" },
		tooltip: { y: [{ formatter: value => `${formatMoney(value, 0)}` }, { formatter: value => `${formatMoney(value, 0)} ${t("cheques").toLowerCase()}` }] },
	}
	const series = [
		{ name: t("sales"), data: chart?.sales || [] },
		{ name: t("cheques"), data: chart?.cheques || [] },
	]

	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<div className="mb-2">
					<h5 className="mb-0" style={{ fontWeight: 800 }}>{t("dashboard_sales_dynamics")}</h5>
					<div style={mutedStyle}>{subtitle}</div>
				</div>
				<ReactApexChart options={options} series={series} type="area" height={360} />
			</div>
		</div>
	)
}

function Operations({ data, t, onItemClick }) {
	const operational = data?.operational || {}
	const summary = data?.summary || {}
	const items = [
		{ key: "closed_cheques", title: t("closed_cheques"), value: data?.total_cheques_closed, sub: `${formatMoney(data?.count_cheques_closed, 0)} ${t("cheques").toLowerCase()}`, color: "var(--dark-fg-success, #16a34a)", clickable: true },
		{ key: "opened_cheques", title: t("opened_cheques"), value: data?.total_cheques_opened, sub: `${formatMoney(data?.count_cheques_opened, 0)} ${t("cheques").toLowerCase()}`, color: "var(--dark-fg-primary, #2563eb)", clickable: true },
		{ key: "deleted_cheques", title: t("deleted_cheques"), value: operational.deleted_cheques_amount, sub: `${formatMoney(operational.deleted_cheques_count, 0)} ${t("cheques").toLowerCase()}`, color: "var(--dark-fg-danger, #dc2626)", clickable: true },
		{ key: "deleted_products", title: t("deleted_products"), value: operational.deleted_products_amount, sub: `${formatMoney(operational.deleted_products_count, 0)} ${t("positions_short")}`, color: "var(--dark-fg-danger, #b91c1c)", clickable: true },
		{ key: "returns", title: "Возвраты", value: operational.returns_amount, sub: `${formatMoney(operational.returns_count, 0)} док. · вычтены из продаж`, color: "var(--dark-fg-danger, #9a3412)", clickable: false },
		{ key: "expenses", title: t("expenses"), value: summary.expenses, sub: `${formatMoney(summary.expense_share_percent, 1)}% ${t("dashboard_of_sales")}`, color: "#e0762c", clickable: false },
		{ key: "surcharge", title: t("surcharge"), value: operational.surcharge, sub: t("service_and_delivery"), color: "var(--dark-fg-violet, #7c3aed)", clickable: false },
	]

	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1" style={{ fontWeight: 800 }}>{t("dashboard_operational_pulse")}</h5>
				<div className="mb-3" style={mutedStyle}>{t("dashboard_operational_pulse_subtitle")}</div>
				<div className="d-flex flex-column gap-2">
					{items.map(item => (
						<div
							className="d-flex justify-content-between align-items-center"
							key={item.key}
							style={{ background: "var(--dark-surface-2, #f8fafc)", borderRadius: "14px", padding: "8px 12px", ...(item.clickable ? { cursor: "pointer" } : {}) }}
							onClick={() => item.clickable && onItemClick?.(item.key, item.title)}
							role={item.clickable ? "button" : undefined}
						>
							<div>
								<div style={{ fontWeight: 800, color: "var(--dark-text-strong, #111827)" }}>{item.title}</div>
								<div style={mutedStyle}>{item.sub}</div>
							</div>
							<div style={{ fontWeight: 900, color: item.color }}>{fullNumber(item.value)}</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

// Возраст показываем, только если год рождения правдоподобный: кассиры иногда вбивают
// в поле ДР дату регистрации, и выходит «исполняется 1 год»
function birthdayAge(age) {
	return age >= 5 && age <= 100 ? age : null
}

function birthdayWhen(daysLeft, t) {
	if (daysLeft === 0) return t("birthday_today")
	if (daysLeft === 1) return t("birthday_tomorrow")
	return t("birthday_in_days", { days: daysLeft })
}

/**
 * Дни рождения гостей бренда: сегодня, неделя, месяц и ближайшие именинники.
 * Клиенты общие на бренд — фильтр филиала дашборда на виджет не влияет.
 * Нет права «Клиенты» — виджет не показываем вовсе.
 */
function BirthdaysCard({ t, navigate }) {
	const [data, setData] = useState(null)

	useEffect(() => {
		if (!hasPermission("clients")) return
		GET("/clients/birthdays-summary", {}, { loader: false }).then(response => {
			if (httpOk(response)) setData(response.data)
		})
	}, [])

	// Дни рождения нужны единицам брендов: карточка видна, только когда бренд включил
	// сбор ДР на странице «Бренд» (режим optional/required), иначе она лишь занимает место
	if (!data || !['optional', 'required'].includes(data.birthday_mode)) return null

	const coverage = data.total > 0 ? (data.with_birthday * 100) / data.total : 0
	const tiles = [
		{ days: 0, title: t("birthday_today"), value: data.today, color: "#ec4899" },
		{ days: 7, title: t("birthday_week"), value: data.week, color: "#7c3aed" },
		{ days: 30, title: t("birthday_month"), value: data.month, color: "#2563eb" },
	]
	const color = "#ec4899"

	return (
		<div className="row g-3 mt-1">
			<div className="col-xl-4 col-md-6">
				<div className="card h-100" style={cardStyle}>
					<div className="card-body">
						<div className="d-flex align-items-center gap-2 mb-3">
							<div className="d-flex align-items-center justify-content-center" style={{ width: "34px", height: "34px", borderRadius: "12px", background: `${color}18`, color }}>
								<i className="uil uil-gift"></i>
							</div>
							<div>
								<h5 className="mb-0" style={{ fontWeight: 800 }}>{t("birthdays")}</h5>
								<div style={mutedStyle}>
									{t("birthday_coverage", {
										filled: formatMoney(data.with_birthday, 0),
										total: formatMoney(data.total, 0),
										percent: formatMoney(coverage, 1),
									})}
								</div>
							</div>
						</div>

						<div className="d-flex gap-2 mb-3">
							{tiles.map(tile => (
								<div
									key={tile.days}
									className="flex-fill text-center"
									role="button"
									style={{ background: "var(--dark-surface-2, #f8fafc)", borderRadius: "14px", padding: "8px 6px", cursor: "pointer" }}
									onClick={() => navigate(`/clients?birthday_days=${tile.days}`)}
								>
									<div style={{ fontWeight: 900, fontSize: "20px", color: tile.color }}>{fullNumber(tile.value)}</div>
									<div style={mutedStyle}>{tile.title}</div>
								</div>
							))}
						</div>

						<div className="d-flex flex-column gap-2">
							{data.upcoming?.length === 0 && (
								<div className="text-muted text-center py-3">
									{data.with_birthday > 0 ? t("birthday_none_soon") : t("birthday_none_set")}
								</div>
							)}
							{data.upcoming?.slice(0, 5).map(item => {
								const age = birthdayAge(item.age)
								return (
									<div key={item.id} className="d-flex justify-content-between gap-2" style={{ background: "var(--dark-surface-2, #f8fafc)", borderRadius: "14px", padding: "8px 12px" }}>
										<div className="text-truncate">
											<div className="text-truncate" style={{ fontWeight: 800, color: "var(--dark-text-strong, #111827)" }}>{item.first_name || t("client_without_name")}</div>
											<div style={mutedStyle}>{regexPhoneNumber(item.phone1)}</div>
										</div>
										<div className="text-end" style={{ whiteSpace: "nowrap" }}>
											<div style={{ fontWeight: 800, color: item.days_left === 0 ? color : "var(--dark-text-strong, #111827)" }}>
												{birthdayWhen(item.days_left, t)}
											</div>
											<div style={mutedStyle}>
												{formatDate(item.next_birthday, "dd.MM")}{age ? ` · ${t("birthday_turns", { age })}` : ""}
											</div>
										</div>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function TopList({ title, subtitle, icon, rows = [], color = "#2563eb", t }) {
	const visibleRows = rows.slice(0, 5)
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex align-items-center gap-2 mb-3">
					<div className="d-flex align-items-center justify-content-center" style={{ width: "34px", height: "34px", borderRadius: "12px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
					<div>
						<h5 className="mb-0" style={{ fontWeight: 800 }}>{title}</h5>
						<div style={mutedStyle}>{subtitle}</div>
					</div>
				</div>

				<div className="d-flex flex-column gap-2">
					{visibleRows.length === 0 && (
						<div className="text-muted text-center py-4">{t("no_data")}</div>
					)}
					{visibleRows.map((item, index) => {
						const percentage = Number(item.percentage || 0)
						return (
							<div key={`${item.name}-${index}`} style={{ background: "var(--dark-surface-2, #f8fafc)", borderRadius: "14px", padding: "10px 12px" }}>
								<div className="d-flex justify-content-between gap-2">
									<div className="text-truncate" style={{ fontWeight: 800, color: "var(--dark-text-strong, #111827)" }}>
										<span className="text-muted me-2">#{index + 1}</span>{item.name || t("without_name")}
									</div>
									<div style={{ fontWeight: 900, color }}>{fullNumber(item.total_amount)}</div>
								</div>
								<div className="d-flex justify-content-between align-items-center mt-2">
									<div style={mutedStyle}>{formatMoney(item.total_quantity, 0)} {t("pcs_short")}</div>
									<div className="flex-grow-1 mx-2" style={{ height: "6px", background: "var(--dark-surface-4, #e5eaf1)", borderRadius: "999px", overflow: "hidden" }}>
										<div style={{ height: "100%", width: `${Math.min(100, percentage)}%`, background: color, borderRadius: "999px" }}></div>
									</div>
									<div style={{ ...mutedStyle, minWidth: "42px", textAlign: "right" }}>{formatMoney(percentage, 1)}%</div>
								</div>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

function shiftLabel(shift) {
	const opened = shift.opened_at ? formatDate(shift.opened_at, "dd.MM HH:mm") : ""
	const closed = shift.closed_at ? formatDate(shift.closed_at, "HH:mm") : "открыта"
	return `№${shift.shift_number} · ${opened} – ${closed}${shift.cashbox_name ? ` · ${shift.cashbox_name}` : ""}`
}

function Index() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [topData, setTopData] = useState(null)
	const [shifts, setShifts] = useState([])
	const [listModal, setListModal] = useState({ show: false, type: null, title: "", rows: [], total_amount: 0, total_quantity: 0 })
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedCheque, setSelectedCheque] = useState({})

	const filters = useAnalyticsFilters({
		mode: "shift",
		...buildPeriod("yesterday"),
		pos_id: reduxAccount.pos_id ?? -1,
		shift_id: -1,
	}, searchWithFilter)
	const { draft, applied, patch, apply, patchApplied } = filters
	const options = useAnalyticsOptions([], draft.pos_id)
	const isShift = applied.mode === "shift"

	// Смена филиала меняет и начало операционного дня: быстрые даты пересчитываем по часу новой точки
	function changePos(id) {
		setBusinessDayStartFromPoses(options.poses, id)
		const period = draft.period && draft.period !== "custom" ? buildPeriod(draft.period) : {}
		patch({ ...period, pos_id: id, shift_id: -1 })
	}

	function requestParams(filter) {
		if (filter.mode === "shift") {
			return { pos_id: filter.pos_id, shift_id: Number(filter.shift_id) > 0 ? filter.shift_id : null }
		}
		return { pos_id: filter.pos_id, start_date: filter.start_date, end_date: filter.end_date }
	}

	async function searchWithFilter(filter) {
		// Дашборд — по одному филиалу: «все филиалы» из общего фильтра сюда не подходят
		if (!filter.pos_id || Number(filter.pos_id) <= 0) return
		const shiftMode = filter.mode === "shift"
		const params = requestParams(filter)
		const [salesResponse, topResponse] = await Promise.all([
			GET(buildAnalyticsUrl(shiftMode ? "/dashboard-z-sales" : "/dashboard-sales", params), {}, { loader: true }),
			GET(buildAnalyticsUrl(shiftMode ? "/dashboard-z-top" : "/dashboard-top", { ...params, include: TOP_INCLUDE }), {}, { loader: false }),
		])
		if (httpOk(salesResponse)) setData(salesResponse.data)
		if (httpOk(topResponse)) setTopData(topResponse.data)
	}

	async function openChequesList(type, title) {
		const params = { type, mode: applied.mode, pos_id: applied.pos_id }
		if (isShift) params.shift_id = data?.meta?.shift_id
		else {
			params.start_date = data?.meta?.start_date || applied.start_date
			params.end_date = data?.meta?.end_date || applied.end_date
		}
		const response = await GET(buildAnalyticsUrl("/dashboard-cheques-list", params), {}, { loader: true })
		if (!httpOk(response)) return
		setListModal({
			show: true,
			type,
			title,
			rows: response.data?.data || [],
			total_amount: response.data?.total_amount || 0,
			total_quantity: response.data?.total_quantity || 0,
		})
	}

	async function openChequeRightbar(item) {
		if (!item?.cheque_id) return
		const response = await GET(`/cheques/${item.cheque_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedCheque(response.data.data)
			setShowRightBar(true)
		}
	}

	// Пришли со «всеми филиалами» с другого экрана или с филиалом, которого нет в этом логине, —
	// берём филиал аккаунта или первый
	useEffect(() => {
		const known = options.poses.some(pos => Number(pos.id) === Number(applied.pos_id))
		if (options.poses.length > 0 && (!applied.pos_id || Number(applied.pos_id) <= 0 || !known)) {
			const fallback = options.poses.find(pos => pos.id === reduxAccount.pos_id) || options.poses[0]
			apply({ ...applied, pos_id: fallback.id, shift_id: -1 })
		}
	}, [options.poses]) // eslint-disable-line react-hooks/exhaustive-deps

	// Смены выбранного в панели филиала
	useEffect(() => {
		let alive = true
		if (!draft.pos_id || Number(draft.pos_id) <= 0) {
			setShifts([])
			return
		}
		GET(`/helper/shifts?pos_id=${draft.pos_id}&limit=30`).then(response => {
			if (alive && httpOk(response)) setShifts((response.data || []).map(shift => ({ id: shift.id, name: shiftLabel(shift) })))
		})
		return () => { alive = false }
	}, [draft.pos_id])

	const kpis = data?.kpis || []
	// Незакрытый день / открытая смена сравниваются с тем же отрезком времени — сервер так и подписывает
	const compareLabel = data?.compare?.available ? data.compare.label : ""
	const isDeletedProducts = listModal.type === "deleted_products"
	const currentShift = shifts.find(shift => shift.id === data?.meta?.shift_id)
	const subtitle = isShift
		? (currentShift ? `Смена ${currentShift.name}` : "Последняя смена кассы по умолчанию")
		: "Чеки — по бизнес-дню закрытия, возвраты вычтены по дате возврата"
	const chartSubtitle = isShift
		? "По часам смены — от открытия до закрытия"
		: applied.start_date === applied.end_date ? "По часам бизнес-дня" : "По дням периода"

	return (
		<>
			<div style={{ paddingBottom: "20px" }}>
				<AnalyticsFilterBar
					title="Дашборд"
					icon="uil-apps"
					subtitle={subtitle}
					filters={filters}
					presets={draft.mode === "day" ? DAY_PRESETS : false}
					tabs={[{ id: "shift", label: t("shift"), icon: "uil-clock" }, { id: "day", label: t("day"), icon: "uil-calendar-alt" }]}
					tabsOnTop activeTab={applied.mode}
					onTabChange={(id) => patchApplied({ mode: id })}
					inline={<>
						<div className="af-inline-item">
							<FilterSelect options={options.poses} value={draft.pos_id} placeholder="Выберите филиал" onChange={changePos} />
						</div>
						{draft.mode === "shift" && (
							<div className="af-inline-item af-inline-wide">
								<FilterSelect options={shifts} allLabel="Последняя смена" value={draft.shift_id} onChange={(id) => patch({ shift_id: id })} />
							</div>
						)}
					</>}
				/>

				<div className="row g-3 mb-3">
					{kpis.map(item => (
						<div className="col-xl-2 col-md-4 col-sm-6" key={item.key}>
							<KpiCard
								item={item}
								title={t(kpiTitleKeys[item.key] || item.key)}
								compareLabel={compareLabel}
								onClick={item.key === "cheques" ? () => openChequesList("cheques", t("cheques")) : undefined}
							/>
						</div>
					))}
				</div>

				<div className="row g-3">
					<div className="col-xl-8">
						<SalesChart chart={data?.chart} t={t} subtitle={chartSubtitle} />
					</div>
					<div className="col-xl-4">
						<Operations data={data} t={t} onItemClick={openChequesList} />
					</div>
				</div>

				<div className="row g-3 mt-1">
					<div className="col-xl-4 col-md-6">
						<TopList title={t("top_order_types")} subtitle="Выручка с обслуживанием за вычетом возвратов" icon="uil-shopping-bag" rows={topData?.order_types?.data || []} color="#2563eb" t={t} />
					</div>
					<div className="col-xl-4 col-md-6">
						<TopList title={t("top_menu")} subtitle="Товарная выручка после скидки чека" icon="uil-restaurant" rows={topData?.menus?.data || []} color="#16a34a" t={t} />
					</div>
					<div className="col-xl-4 col-md-6">
						<TopList title={t("top_products")} subtitle="Товарная выручка после скидки чека" icon="uil-utensils" rows={topData?.products?.data || []} color="#e0762c" t={t} />
					</div>
				</div>

				<BirthdaysCard t={t} navigate={navigate} />
			</div>

			<Modal show={listModal.show} onHide={() => setListModal({ ...listModal, show: false })} size="lg" centered>
				<Modal.Header closeButton>
					<Modal.Title>{listModal.title}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ maxHeight: "70vh", overflow: "auto" }}>
					{listModal.rows.length === 0 ? (
						<div className="text-muted text-center py-4">{t("no_data")}</div>
					) : (
						<div className="table-responsive">
							<table className="table table-bordered table-bordered-custom mb-0">
								<thead>
									<tr>
										<th style={{ minWidth: "33px" }} className="text-center">#</th>
										{isDeletedProducts ? (
											<>
												<th className="text-start"><b>{t("name2")}</b></th>
												<th className="text-center"><b>{t("time")}</b></th>
												<th className="text-end"><b>№</b></th>
												<th className="text-end"><b>{t("quantity")}</b></th>
												<th className="text-end"><b>{t("amount")}</b></th>
											</>
										) : (
											<>
												<th className="text-start"><b>{t("cashier")}</b></th>
												<th className="text-start"><b>{t("waiter")}</b></th>
												<th className="text-center"><b>{t("time")}</b></th>
												<th className="text-end"><b>№</b></th>
												<th className="text-end"><b>{t("amount")}</b></th>
											</>
										)}
									</tr>
								</thead>
								<tbody>
									{listModal.rows.map((item, index) => (
										<tr key={`${item.cheque_id}-${index}`} style={{ cursor: "pointer" }} onClick={() => openChequeRightbar(item)}>
											<td className="text-center">{index + 1}</td>
											{isDeletedProducts ? (
												<>
													<td className="text-start td-ellipsis">{item.name}</td>
													<td className="text-center">{formatDate(item.created_at, "HH:mm")}</td>
													<td className="text-end">{item.cheque_number}</td>
													<td className="text-end">{formatMoney(item.quantity, 0)}</td>
													<td className="text-end">{formatMoney(item.total_amount)}</td>
												</>
											) : (
												<>
													<td className="text-start td-ellipsis">{item.cashier_name}</td>
													<td className="text-start td-ellipsis">{item.waiter_name}</td>
													<td className="text-center">{formatDate(item.cheque_closed_at || item.cheque_opened_at, "HH:mm")}</td>
													<td className="text-end">{item.cheque_number}</td>
													<td className="text-end">{formatMoney(item.total_amount)}</td>
												</>
											)}
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td></td>
										<td><b>{t("total")}</b></td>
										<td></td>
										<td></td>
										{isDeletedProducts ? (
											<>
												<td className="text-end"><b>{formatMoney(listModal.total_quantity, 0)}</b></td>
												<td className="text-end"><b>{formatMoney(listModal.total_amount)}</b></td>
											</>
										) : (
											<>
												<td></td>
												<td className="text-end"><b>{formatMoney(listModal.total_amount)}</b></td>
											</>
										)}
									</tr>
								</tfoot>
							</table>
						</div>
					)}
				</Modal.Body>
			</Modal>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={selectedCheque} />
		</>
	)
}

export default Index
